/**
 * Human-friendly display name for an agent, from its raw `logicalId` / `agentId`.
 *
 * The engine dispatches + publishes agents by their manifest `logicalId` — a CamelCase
 * identifier like `AnalystRatingsSpecialistAgent`. That id is fine on the wire but ugly in the
 * UI (it was showing verbatim as every turn-panel header). This turns it into readable words:
 *
 *   AnalystRatingsSpecialistAgent → "Analyst Ratings Specialist"
 *   FundamentalsLeadAgent         → "Fundamentals Lead"
 *   __roomContext__               → "Shared"      (reserved pseudo-agent)
 *   BuilderAgent / builder        → "Builder"
 *
 * Pure + defensive: non-string / empty → "Agent"; a value that's already spaced/friendly is
 * left alone. This is display-only — never use the result as an id.
 */

const RESERVED = Object.freeze({
  __roomcontext__: 'Shared',
  shared: 'Shared',
  builderagent: 'Builder',
  builder: 'Builder',
  judge: 'Judge',
  agent: 'Agent',
});

/**
 * @param {unknown} raw The agent logicalId / agentId.
 * @returns {string} A readable label (never empty; falls back to "Agent").
 */
export function humanizeAgentName(raw) {
  if (typeof raw !== 'string') return 'Agent';
  const id = raw.trim();
  if (!id) return 'Agent';

  const reserved = RESERVED[id.toLowerCase()];
  if (reserved) return reserved;

  // Already contains a space or looks like a sentence → assume it's already friendly.
  if (/\s/.test(id)) return id;

  let s = id;
  // Split camelCase / PascalCase and acronym boundaries: "APIKeyAgent" → "API Key Agent".
  s = s
    .replace(/[_-]+/g, ' ')                       // snake / kebab → spaces
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')       // fooBar → foo Bar
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')    // APIKey → API Key
    .trim();

  // Drop a trailing "Agent" (every logicalId ends with it → noise): "Analyst Ratings Agent"
  // → "Analyst Ratings". Keep it if that would leave nothing.
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length > 1 && /^agents?$/i.test(words[words.length - 1])) {
    words.pop();
  }
  return words.join(' ') || 'Agent';
}
